import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import VideoCard from '@/components/feature/VideoCard';
import { getCategories, getVideosByCategory, searchVideos, sortVideos, type SortKey } from '@/utils/video';

const PAGE_SIZE = 6;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [sortKey, setSortKey] = useState<SortKey>('latest');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const results = useMemo(() => (query ? searchVideos(query) : []), [query]);
  const sorted = useMemo(() => sortVideos(results, sortKey), [results, sortKey]);
  const visibleVideos = sorted.slice(0, visibleCount);

  useEffect(() => {
    setInput(query);
    setVisibleCount(PAGE_SIZE);
  }, [query]);

  useEffect(() => {
    document.title = query ? `搜索"${query}"的结果 - 宅男视频` : '搜索视频 - 宅男视频';
  }, [query]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  const suggestions = getCategories().map((cat) => cat.name);
  const hotKeywords = ['新番', '机甲', '五杀', '反转', '无伤', '治愈'];

  return (
    <div className="w-full">
      <div className="w-full bg-primary-900">
        <div className="w-full px-4 md:px-6 py-10 md:py-14">
          <h1 className="text-2xl md:text-3xl font-bold text-background-50">
            {query ? (
              <>
                搜索：<span className="text-accent-400">{query}</span>
              </>
            ) : (
              '搜索视频'
            )}
          </h1>
          <p className="mt-2 text-sm text-primary-100">输入标题、分类或标签关键词，快速找到想看的内容</p>

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                type="search"
                name="q"
                placeholder="试试搜索：新番、五杀、反转…"
                className="w-full h-12 pl-11 pr-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-md bg-accent-500 text-primary-950 font-semibold text-sm whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-primary-200">热门搜索：</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setSearchParams({ q: word })}
                className="px-3 py-1 rounded-full bg-primary-800 text-primary-100 text-xs hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-8 md:py-10">
        {query ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
              <p className="text-sm text-foreground-600">
                找到 <span className="font-semibold text-foreground-950">{results.length}</span> 个与"
                <span className="font-semibold text-foreground-950">{query}</span>"相关的结果
              </p>
              {results.length > 0 && (
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200 overflow-x-auto">
                  <button
                    type="button"
                    onClick={() => setSortKey('latest')}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      sortKey === 'latest' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    <i className="ri-time-line"></i>
                    最新
                  </button>
                  <button
                    type="button"
                    onClick={() => setSortKey('views')}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      sortKey === 'views' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    <i className="ri-fire-line"></i>
                    最热
                  </button>
                </div>
              )}
            </div>

            {visibleVideos.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {visibleVideos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
                {visibleCount < sorted.length && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 h-11 px-8 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                      加载更多
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="mx-auto max-w-lg text-center py-16">
                <span className="mx-auto flex items-center justify-center w-20 h-20 rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-search-eye-line text-4xl"></i>
                </span>
                <h2 className="mt-6 text-lg font-bold text-foreground-950">没有找到相关视频</h2>
                <p className="mt-3 text-sm text-foreground-600">
                  换个关键词试试，或者从下面这些建议中挑选：
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {suggestions.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setSearchParams({ q: name })}
                      className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-accent-300 hover:text-accent-600 transition-colors cursor-pointer"
                    >
                      {name}
                    </button>
                  ))}
                </div>
                <Link
                  to="/"
                  className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-foreground-800 font-medium text-sm whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className="py-6">
            <h2 className="text-lg font-bold text-foreground-950 mb-4">按分类浏览</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {getCategories().map((cat) => {
                const count = getVideosByCategory(cat.slug).length;
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex items-center gap-3 rounded-lg bg-background-50 border border-background-200 p-4 hover:border-accent-300 transition-colors cursor-pointer"
                  >
                    <span className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary-100 text-primary-700 shrink-0">
                      <i className={`${cat.icon} text-xl`}></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950 group-hover:text-accent-600 transition-colors">{cat.name}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">共 {count} 个视频</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}